import React, { useState, createContext, useRef, useEffect, useMemo } from "react";
import {
  Table as MUITable,
  TableContainer,
  TableBody,
  TableHead,
  TableCell,
  Paper,
  TableRow,
  TableSortLabel,
  TablePagination,
  Box,
  Grid,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Empty, { IEmptyProps } from "./Empty";
import { ArrowDownward } from "@mui/icons-material";
import _debounce from "lodash.debounce";

type IColumn = {
  header: string;
  key: string;
  sort?: boolean;
  // align?: "inherit" | "left" | "center" | "right" | "justify" | undefined;
  align?: any;
  disableCellClick?: boolean;
  size?: "small" | "medium";
  width?: number | string;
  maxWidth?: number | string;
};

export type IPagination = {
  total?: number;
  pageSize: number;
  currentPage: number;
  rowsPerPageOptions: number[];
};

export type selectedCheckboxType = Record<string, any>;
type variant = "outlined" | "contained" | "text";

type TableDataRowProps = {
  row: selectedCheckboxType;
  rowIndex: number;
  columns: IColumn[];
  checkbox?: boolean;
  hover?: boolean;
  showCheckBox?: boolean;
  rowStyle?: Record<string, any>;
  selectedField?: selectedCheckboxType[];
  checkedIds?: Array<string | number>;
  getTableRowColor: (row: Record<string, any>) => string;
  handleClick: (event: React.ChangeEvent<HTMLInputElement>, name: selectedCheckboxType) => void;
  handleHoveredRow: (id: number | string | null) => void;
  hoveredRowId: number | string | null;
  onRowItemClick?: (row: any) => void;
  onRowItemDoubleClick?: (row: any) => void;
  onRowMouseEnter?: (v: number) => void;
  onRowMouseLeave?: (v: number) => void;
};

const getCheckboxOpacity = (isUserSelected: boolean, showCheckBox?: boolean) => {
  if (isUserSelected) return 1;
  if (showCheckBox) return 0;
  return 1;
};

const TableDataRow = ({
  row,
  rowIndex,
  columns,
  checkbox,
  hover,
  showCheckBox,
  rowStyle,
  selectedField,
  checkedIds,
  getTableRowColor,
  handleClick,
  handleHoveredRow,
  hoveredRowId,
  onRowItemClick,
  onRowItemDoubleClick,
  onRowMouseEnter,
  onRowMouseLeave,
}: TableDataRowProps) => {
  const isUserSelected = selectedField?.map((f) => f?.id)?.includes(row?.id) ?? false;
  const isCheckboxHidden = row.isMerged || row?.disabled || row?.noCheckFolder;

  return (
    <TableRow
      key={row.id}
      hover={hover}
      selected={checkedIds?.includes(row?.id) || row?.isMerged}
      sx={{
        ...rowStyle,
        cursor: "pointer",
        ":hover": { ".checkbox": { opacity: 1 } },
        opacity: row?.disabled ? 0.3 : 1,
        "&.Mui-selected": {
          background: getTableRowColor(row),
          "&:hover": { background: getTableRowColor(row) },
        },
      }}
      onMouseEnter={() => {
        handleHoveredRow(row?.id);
        onRowMouseEnter?.(row?.id);
      }}
      onMouseLeave={() => {
        if (hoveredRowId != null) handleHoveredRow(null);
        onRowMouseLeave?.(rowIndex);
      }}
    >
      {checkbox && (isCheckboxHidden ? <span /> : (
        <TableCell>
          <Checkbox
            className="checkbox"
            sx={{ opacity: getCheckboxOpacity(!!isUserSelected, showCheckBox) }}
            checked={isUserSelected}
            disabled={isCheckboxHidden}
            onChange={(event) => {
              if (isCheckboxHidden) return;
              handleClick(event, row);
            }}
            inputProps={{ "aria-label": "select a user" }}
          />
        </TableCell>
      ))}
      {columns?.map(({ key, align, disableCellClick }) => (
        <TableCell
          key={key}
          component={key === "id" ? "td" : undefined}
          scope={key === "id" ? "row" : undefined}
          onDoubleClick={() => onRowItemDoubleClick?.(row)}
          onClick={() => {
            if (onRowItemClick && (key === "id" || !disableCellClick)) onRowItemClick(row);
          }}
          align={align}
        >
          {row[key] || "--"}
        </TableCell>
      ))}
    </TableRow>
  );
};

type IProps = {
  columns: IColumn[];
  data: any[];
  onRowItemClick?: (row: any) => void;
  onRowItemDoubleClick?: (row: any) => void;
  pagination?: IPagination;
  empty?: IEmptyProps | any;
  loading?: boolean;
  onSortClick?: (header: string) => void;
  onPageChange?: (event: unknown, newPage: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  checkbox?: boolean;
  showCheckboxOnHeader?: boolean;
  getCheckBoxValues?: (state: selectedCheckboxType) => void;
  search?: any;
  sx?: Record<string, any>;
  rowStyle?: Record<string, any>;
  hover?: boolean;
  size?: "small" | "medium";
  showCheckBox?: boolean;
  onRowMouseEnter?: (v: number) => void;
  onRowMouseLeave?: (v: number) => void;
  showCheckStrictlyOnHover?: boolean;
  children?: React.ReactNode;
  emptyBtnVariant?: variant;
};

const Table: React.FC<IProps> = ({
  columns,
  data,
  onRowItemClick,
  onRowItemDoubleClick,
  pagination,
  empty,
  loading,
  onPageChange,
  onSortClick,
  onRowsPerPageChange,
  checkbox = false,
  showCheckboxOnHeader = false,
  sx,
  rowStyle,
  hover = false,
  showCheckBox = false,
  size,
  showCheckStrictlyOnHover = false,
  onRowMouseEnter,
  onRowMouseLeave,
  emptyBtnVariant,
  children,
  getCheckBoxValues,
}: IProps) => {
  const {
    records: selectedField,
    changeRecords: setSelectedField,
    hoveredRowId,
    handleHoveredRow,
  } = React.useContext(TableRecordsContext);
  const [isAssendeing, setIsAssending] = useState(false);
  const [clickedValue, setClickedValue] = useState("Date");
  const rowCount = data?.length;
  const allSelected = selectedField?.length;


  const handleClick = (event: React.ChangeEvent<HTMLInputElement>, name: selectedCheckboxType) => {
    if (event.target.checked) {
      setSelectedField((prev: any) => [...prev, name]);
      getCheckBoxValues?.([...(Array.isArray(selectedField) ? selectedField : []), name]);
    } else {
      const filtered = selectedField.filter((val: selectedCheckboxType) => val.id !== name?.id);
      setSelectedField(filtered);
      getCheckBoxValues?.(filtered);
    }
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected: Record<string, any> = data;
      setSelectedField(newSelected);
      getCheckBoxValues?.(newSelected);
      return;
    }
    setSelectedField([]);
    getCheckBoxValues?.([]);
  };

  // this is specific to Reconciliation page only
  const checkedIds = selectedField?.map((res: any) => res?.id);
  const getTableRowColor = (row: Record<string, any>): string => {
    if (checkedIds?.includes(row?.id)) return "#FFF3DB";
    if (row?.merged) return "#EDF9F0";
    if (row?.disabled) return "#0000FF";
    return "#fff";
  };
  const simpleHash = (data: any): string => {
    if (!data) return "";

    // Create a hash based on the 'id' field or other key fields in each row
    const dataString = data?.map((item: any) => item?.id).join(",");
    return dataString; // This could also be more complex depending on your needs
  };
  const clearSelections = React.useCallback(() => {
    setSelectedField([]);
    getCheckBoxValues?.([]);
  }, [getCheckBoxValues, setSelectedField]);

  const prevDataHashRef = useRef<string | null>(null);

  useEffect(() => {
    if (!loading && (data?.length ?? 0) > 0) {
      const currentDataHash = simpleHash(data);
      if (prevDataHashRef.current !== currentDataHash) {
        clearSelections();
        prevDataHashRef.current = currentDataHash;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <TableContainer component={Paper} elevation={0} square sx={{ ...sx }}>
          <MUITable stickyHeader sx={{ minWidth: 700 }} size={size}>
            <TableHead style={{}}>
              <TableRow>
                {checkbox && (
                  <TableCell sx={{ width: "2px" }}>
                    <Checkbox
                      sx={{
                        opacity: showCheckboxOnHeader ? 1 : 0,
                      }}
                      hidden={!showCheckboxOnHeader}
                      disabled={!showCheckboxOnHeader}
                      indeterminate={allSelected > 0 && allSelected < rowCount}
                      checked={rowCount > 0 && allSelected === rowCount}
                      inputProps={{ "aria-label": "select a user" }}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                )}
                {columns?.map(({ header, sort, align, width, maxWidth }, index) => {
                  return (
                    <React.Fragment key={header}>
                      {sort ? (
                        <TableCell
                          align={align}
                          sx={{
                            width,
                            backgroundColor: (theme) => theme.palette.common.white,
                          }}
                        >
                          <TableSortLabel
                            active={clickedValue === header}
                            onClick={(e) => {
                              if (onSortClick) {
                                setIsAssending(!isAssendeing);
                                setClickedValue(header);
                                onSortClick(isAssendeing ? `-${header}` : header);
                              }
                            }}
                            IconComponent={isAssendeing ? ArrowUpwardIcon : ArrowDownward}
                            style={{ fontWeight: "bold", color: "black" }}
                          >
                            {header}
                          </TableSortLabel>
                        </TableCell>
                      ) : (
                        <TableCell
                          sx={{
                            width,
                            maxWidth,
                            fontWeight: "bold",
                            color: "black",
                            backgroundColor: (theme) => theme.palette.common.white,
                          }}
                          align={align}
                        >
                          {header}
                        </TableCell>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    component="td"
                    scope="row"
                    colSpan={columns.length}
                    sx={{ textAlign: "center", height: "55vh" }}
                  >
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {children}
                  {(data?.length ?? 0) > 0
                    ? data?.map((row: selectedCheckboxType, i: number) => (
                        <TableDataRow
                          key={row.id}
                          row={row}
                          rowIndex={i}
                          columns={columns}
                          checkbox={checkbox}
                          hover={hover}
                          showCheckBox={showCheckBox}
                          rowStyle={rowStyle}
                          selectedField={Array.isArray(selectedField) ? selectedField : []}
                          checkedIds={checkedIds}
                          getTableRowColor={getTableRowColor}
                          handleClick={handleClick}
                          handleHoveredRow={handleHoveredRow}
                          hoveredRowId={hoveredRowId}
                          onRowItemClick={onRowItemClick}
                          onRowItemDoubleClick={onRowItemDoubleClick}
                          onRowMouseEnter={onRowMouseEnter}
                          onRowMouseLeave={onRowMouseLeave}
                        />
                      ))
                    : empty.renderComponent || (
                        <TableRow>
                          <TableCell
                            component="td"
                            scope="row"
                            colSpan={columns.length}
                            sx={{ textAlign: "center", height: "55vh" }}
                          >
                            <>
                              {empty && (
                                <Empty
                                  title={empty.title}
                                  description={empty.description}
                                  onAddNewClick={empty.onAddNewClick}
                                  btnVariant={emptyBtnVariant}
                                  icon={empty.icon}
                                  renderComponent={empty.renderComponent}
                                />
                              )}
                            </>
                          </TableCell>
                        </TableRow>
                      )}
                </>
              )}
            </TableBody>
          </MUITable>
        </TableContainer>

        {(data?.length ?? 0) > 0 && (
          <Box
            component={Paper}
            elevation={0}
            square
            display="flex"
            justifyContent="flex-end"
            width="100%"
          >
            {onPageChange && pagination?.total && (
              <TablePagination
                component="div"
                sx={{ width: "100%" }}
                rowsPerPageOptions={pagination?.rowsPerPageOptions}
                count={pagination?.total}
                rowsPerPage={pagination?.pageSize}
                page={pagination?.currentPage}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
              />
            )}
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default Table;

type contextProps = {
  records: selectedCheckboxType;
  changeRecords: (val: selectedCheckboxType) => void;
  hoveredRowId: number | string | null;
  handleHoveredRow: (val: number | string | null) => void;
};

// @ts-ignore
export const TableRecordsContext = createContext<contextProps>({});

export const TableRecordsProvider = ({ children }: any) => {
  const [records, setRecords] = useState<selectedCheckboxType>([]);
  const [hoveredRowId, setHoveredRowId] = useState<number | string | null>(null);

  const handleChangeRecords = (records: any) => {
    setRecords(records);
  };

  const handleHoveredRow = _debounce((id: number | string | null) => {
    setHoveredRowId(id);
  }, 100);

  const contextValue = useMemo(
    () => ({
      records,
      changeRecords: handleChangeRecords,
      hoveredRowId,
      handleHoveredRow,
    }),
    [records, hoveredRowId, handleChangeRecords, handleHoveredRow]
  );

  return (
    <TableRecordsContext.Provider value={contextValue}>
      {children}
    </TableRecordsContext.Provider>
  );
};
